// IO bridge between the async game logic and the React touch UI.
// The game logic awaits promises; taps on the canvas / control buttons
// resolve them. A control-state callback drives the contextual buttons.
export class GameAbort extends Error {
  constructor() {
    super("game aborted");
    this.gameAbort = true;
  }
}

export class IO {
  constructor(screen, sound, setControls) {
    this.screen = screen;
    this.sound = sound;
    this.setControls = setControls || (() => {});
    this.mode = null;
    this.resolve = null;
    this.reject = null;
    this.optionRects = null;
    this.optionRestore = true;
    this.numState = null;
    this.helpRenderer = null;
    this.helpSnapshot = null;
    this.helpSavedControls = null;
    this.aborted = false;
    this.keySel = null;
  }

  _begin(mode, controls) {
    if (this.aborted) throw new GameAbort();
    this.mode = mode;
    const ctl = { mode, ...controls };
    this.lastControls = ctl;
    this.setControls(ctl);
    return new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  _finish(value) {
    const r = this.resolve;
    this.mode = null;
    this.resolve = null;
    this.reject = null;
    this.setControls({ mode: null });
    if (r) r(value);
  }

  abort() {
    this.aborted = true;
    const rej = this.reject;
    this.mode = null;
    this.resolve = null;
    this.reject = null;
    this.setControls({ mode: null });
    if (rej) rej(new GameAbort());
  }

  // ---- help overlay (F10 equivalent) ----
  setHelpRenderer(fn) {
    this.helpRenderer = fn;
    this.setControls(this.lastControls || { mode: this.mode });
  }

  get helpAvailable() {
    return !!this.helpRenderer;
  }

  toggleHelp() {
    if (!this.helpRenderer) return;
    if (this.helpSnapshot) {
      this.screen.restore(this.helpSnapshot);
      this.helpSnapshot = null;
      this.setControls(this.helpSavedControls || { mode: this.mode });
      return;
    }
    this.helpSnapshot = this.screen.snapshot();
    this.helpSavedControls = this.lastControls;
    this.helpRenderer();
    this.setControls({ mode: "help" });
  }

  get helpOpen() {
    return !!this.helpSnapshot;
  }

  // ---- input primitives ----

  // Wait for a tap (Enter). Returns 'enter'.
  waitKey(label) {
    return this._begin("key", { label: label || "המשך" });
  }

  // Two-way choice rendered as buttons (e.g. end-day / back)
  choiceKeys(keys) {
    return this._begin("choice", { keys });
  }

  // Tap-to-select from on-screen rects (pixel coords on the 320x200 canvas).
  // Returns 1-based index, or -1 for back/escape.
  // `labels` (optional): Hebrew labels mirrored as side-panel buttons; when
  // omitted, side buttons are auto-numbered "1", "2", ... per non-null rect.
  chooseOption(rects, { escapable = true, restore = true, escLabel = "חזרה", actions = [], labels = null } = {}) {
    this.optionRects = rects;
    this.optionRestore = restore;
    this.keySel = null;
    const optionButtons = [];
    rects.forEach((r, i) => {
      if (!r) return;
      const idx = i + 1;
      const lbl = (labels && labels[i]) || String(idx);
      optionButtons.push({ idx, label: lbl });
    });
    return this._begin("options", { escapable, escLabel, actions, optionButtons });
  }

  // side-panel action button shown during an options prompt (e.g. "sell all")
  pressAction(value) {
    if (this.mode !== "options") return;
    if (this.keySel != null && this.optionRects) {
      const kr = this.optionRects[this.keySel];
      if (kr) this.screen.invertRect(kr.x1, kr.y1, kr.x2, kr.y2);
      this.keySel = null;
    }
    this.optionRects = null;
    this.sound.tap();
    this._finish(value);
  }

  // side-panel button mirroring one of the on-canvas option rects (1-based)
  pressOptionButton(idx) {
    if (this.mode !== "options" || !this.optionRects) return;
    const r = this.optionRects[idx - 1];
    if (!r) return;
    if (this.keySel != null) {
      const kr = this.optionRects[this.keySel];
      if (kr) this.screen.invertRect(kr.x1, kr.y1, kr.x2, kr.y2);
      this.keySel = null;
    }
    this._selectOption(idx, r);
  }

  // Numeric input echoed on canvas at grid (col,row). Quick buttons may be
  // provided: [{label, value}]. Returns the number, or -1 when cancelled.
  inputNumber({ col, row, maxDigits, quick = [], escapable = true }) {
    this.numState = { col, row, maxDigits, digits: "" };
    return this._begin("number", { quick: quick.filter((q) => q.value > 0), escapable });
  }

  yesNo({ col, row }) {
    this.ynPos = { col, row };
    return this._begin("yesno", {});
  }

  inputName({ maxLen = 16, onChange }) {
    this.nameOnChange = onChange;
    return this._begin("name", { maxLen });
  }

  // ---- event handlers called from React ----

  handleCanvasTap(x, y) {
    if (this.helpSnapshot) {
      this.toggleHelp();
      return;
    }
    if (this.mode === "key") {
      this.sound.tap();
      this._finish("enter");
    } else if (this.mode === "options" && this.optionRects) {
      if (this.keySel != null) {
        const kr = this.optionRects[this.keySel];
        if (kr) this.screen.invertRect(kr.x1, kr.y1, kr.x2, kr.y2);
        this.keySel = null;
      }
      for (let i = 0; i < this.optionRects.length; i++) {
        const r = this.optionRects[i];
        if (!r) continue;
        // generous touch padding around the original rects
        if (x >= r.x1 - 4 && x <= r.x2 + 4 && y >= r.y1 - 5 && y <= r.y2 + 5) {
          this._selectOption(i + 1, r);
          return;
        }
      }
    }
  }

  async _selectOption(idx, r) {
    const rects = this.optionRects;
    this.optionRects = null;
    this.sound.tap();
    this.screen.invertRect(r.x1, r.y1, r.x2, r.y2);
    await new Promise((res) => setTimeout(res, 160));
    if (this.optionRestore) this.screen.invertRect(r.x1, r.y1, r.x2, r.y2);
    void rects;
    this._finish(idx);
  }

  // ---- keyboard navigation over on-screen options (arrow keys) ----
  keyNavOption(delta) {
    if (this.mode !== "options" || !this.optionRects) return;
    const idxs = [];
    this.optionRects.forEach((r, i) => {
      if (r) idxs.push(i);
    });
    if (idxs.length === 0) return;
    let pos;
    if (this.keySel == null) {
      pos = delta > 0 ? 0 : idxs.length - 1;
    } else {
      pos = (idxs.indexOf(this.keySel) + delta + idxs.length) % idxs.length;
      const pr = this.optionRects[this.keySel];
      this.screen.invertRect(pr.x1, pr.y1, pr.x2, pr.y2);
    }
    const next = idxs[pos];
    const r = this.optionRects[next];
    this.screen.invertRect(r.x1, r.y1, r.x2, r.y2);
    this.keySel = next;
    this.sound.tap();
  }

  keyConfirmOption() {
    if (this.mode !== "options" || this.keySel == null || !this.optionRects) return false;
    const idx = this.keySel;
    this.keySel = null;
    const r = this.optionRects[idx];
    // remove the keyboard highlight; _selectOption re-flashes it like a tap
    this.screen.invertRect(r.x1, r.y1, r.x2, r.y2);
    this._selectOption(idx + 1, r);
    return true;
  }

  pressEscape() {
    if (this.helpSnapshot) {
      this.toggleHelp();
      return;
    }
    if (this.mode === "options") {
      if (this.keySel != null && this.optionRects) {
        const kr = this.optionRects[this.keySel];
        if (kr) this.screen.invertRect(kr.x1, kr.y1, kr.x2, kr.y2);
        this.keySel = null;
      }
      this.optionRects = null;
      this._finish(-1);
    } else if (this.mode === "number") {
      this._clearNumberEcho();
      this.numState = null;
      this._finish(-1);
    }
  }

  pressChoice(key) {
    if (this.mode === "choice") {
      this.sound.tap();
      this._finish(key);
    }
  }

  _echoNumber() {
    const st = this.numState;
    const pad = st.digits + " ".repeat(Math.max(0, st.maxDigits - st.digits.length));
    this.screen.writeAt(st.col, st.row, pad);
  }

  _clearNumberEcho() {
    const st = this.numState;
    if (st) this.screen.writeAt(st.col, st.row, " ".repeat(st.maxDigits));
  }

  pressDigit(d) {
    const st = this.numState;
    if (this.mode !== "number" || !st) return;
    if (st.digits.length >= st.maxDigits) {
      this.sound.errorBeep();
      return;
    }
    if (st.digits === "0") st.digits = ""; // leading zero collapses like the original
    st.digits += String(d);
    this.sound.tap();
    this._echoNumber();
  }

  pressBackspace() {
    const st = this.numState;
    if (this.mode !== "number" || !st || st.digits.length === 0) return;
    st.digits = st.digits.slice(0, -1);
    this._echoNumber();
  }

  pressNumberConfirm() {
    const st = this.numState;
    if (this.mode !== "number" || !st) return;
    if (st.digits.length === 0) {
      this.sound.errorBeep();
      return;
    }
    const value = parseInt(st.digits, 10);
    this.numState = null;
    this.sound.tap();
    this._finish(value);
  }

  pressQuick(value) {
    const st = this.numState;
    if (this.mode !== "number" || !st) return;
    st.digits = String(Math.floor(value));
    this._echoNumber();
    this.numState = null;
    this.sound.tap();
    this._finish(Math.floor(value));
  }

  pressYes() {
    if (this.mode !== "yesno") return;
    this.sound.tap();
    this.screen.writeAt(this.ynPos.col, this.ynPos.row, "ןכ");
    setTimeout(() => this._finish(true), 140);
    this.mode = "yesno-done";
  }

  pressNo() {
    if (this.mode !== "yesno") return;
    this.sound.tap();
    this.screen.writeAt(this.ynPos.col, this.ynPos.row, "אל");
    setTimeout(() => this._finish(false), 140);
    this.mode = "yesno-done";
  }

  nameChanged(value) {
    if (this.nameOnChange) this.nameOnChange(value);
  }

  submitName(value) {
    if (this.mode !== "name") return;
    const v = value.trim().slice(0, 16);
    if (!v) {
      this.sound.errorBeep();
      return;
    }
    this.sound.tap();
    this._finish(v);
  }
}

export function sleep(ms) {
  return new Promise((res) => setTimeout(res, ms));
}
